import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { outpostStyles, OutpostNavbar, BlurText } from '../components/OutpostShared';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiArrowRight, FiTarget, FiTrendingUp, FiSearch, FiMail, 
  FiBarChart2, FiCheck, FiPlus, FiMinus, FiActivity, 
  FiPieChart, FiCrosshair, FiDatabase, FiLayers, FiDollarSign 
} = FiIcons;

// --- DATA ARRAYS ---

const stats = [
  { value: "26%", desc: "Of the average marketing budget is wasted on channels with zero measurable return." },
  { value: "<30%", desc: "Of companies can accurately attribute revenue to specific campaigns." },
  { value: "90.6%", desc: "Of all web pages get zero organic traffic. Visibility isn't accidental." },
  { value: "97%", desc: "Of traffic leaves without buying—unless the funnel is engineered to convert." },
  { value: "20%", desc: "Faster growth for companies that align marketing with revenue goals." }
];

const capabilities = [
  {
    icon: FiActivity,
    title: "Conversion Rate Optimization",
    desc: "Your traffic is already costing you money. We make it work harder. Through systematic A/B testing, UX analysis, and funnel mapping, we turn more existing visitors into customers. A 1% improvement can mean a 10–20% revenue lift."
  },
  {
    icon: FiMail,
    title: "Email Marketing",
    desc: "The highest-ROI channel in digital marketing. We design and write email campaigns and flows that welcome new customers, bring shoppers back, and keep your audience engaged. Segmented, personalized, and optimized. Average return: $36–$42 for every $1 spent."
  },
  {
    icon: FiTarget,
    title: "Marketing Strategy",
    desc: "Before a single campaign launches, we build the foundation. Market analysis, audience segmentation, budget allocation, and a phased growth roadmap. Strategy is the operating system that drives every decision."
  },
  {
    icon: FiDollarSign,
    title: "Paid Advertising",
    desc: "Engineered campaigns across Google, Meta, TikTok, LinkedIn, and programmatic channels—designed for maximum ROAS. From prospecting to retargeting, every campaign is structured and optimized daily."
  },
  {
    icon: FiSearch,
    title: "Search Engine Optimization",
    desc: "The compounding growth channel. We build organic visibility through technical SEO, content strategy, and link building—driving qualified traffic that doesn't cost per click and creates a durable competitive moat."
  }
];

const benefits = [
  "Revenue Attribution — Know exactly which channels and campaigns drive sales, not just clicks.",
  "Lower CAC — Integrated strategy and continuous optimization reduce acquisition costs over time.",
  "Compounding Returns — SEO and email build long-term assets that generate revenue without ad spend.",
  "Precision Targeting — Reach the right audience at the right moment across the right platforms.",
  "Continuous Improvement — Weekly optimizations, monthly reviews, and transparent reporting.",
  "Scalable Growth — Systems built to scale from $10K/month to $1M/month in spend without breaking."
];

const processSteps = [
  {
    step: "01",
    title: "AUDIT",
    desc: "We analyze your current performance, tracking setup, competitive landscape, and customer journey to identify leaks and opportunities."
  },
  {
    step: "02",
    title: "STRATEGIZE",
    desc: "We build a comprehensive strategy with clear KPIs, channel priorities, budget allocation, and a phased growth roadmap tied to revenue."
  },
  {
    step: "03",
    title: "BUILD",
    desc: "We set up campaign infrastructure—ad accounts, tracking pixels, dashboards, email flows, SEO foundations, and A/B testing frameworks."
  },
  {
    step: "04",
    title: "LAUNCH",
    desc: "We deploy campaigns across prioritized channels with precise targeting, compelling creative, and optimized bidding strategies."
  },
  {
    step: "05",
    title: "OPTIMIZE",
    desc: "We enter a continuous improvement cycle—analyzing data daily, testing weekly, and refining strategy quarterly to compound growth."
  }
];

const metrics = [
  { metric: "Email Marketing ROI", insight: "$36–$42 average return for every $1 spent — the highest ROI of any digital channel." },
  { metric: "CRO Impact", insight: "A 1% conversion rate improvement can generate a 10–20% increase in revenue at scale." },
  { metric: "Paid + Organic Synergy", insight: "Companies running SEO and paid search together see 25–30% higher click-through rates." },
  { metric: "Marketing Attribution", insight: "Businesses with proper attribution models are 1.6x more likely to achieve above-average growth." },
  { metric: "SEO Long-Term Value", insight: "Organic search drives 53% of all website traffic — the single largest digital traffic source." }
];

const platforms = [
  { category: "Advertising Platforms", items: ["Google Ads", "Meta Ads (FB/IG)", "TikTok Ads", "LinkedIn Ads", "Amazon Ads", "Pinterest Ads"] },
  { category: "Marketing & Analytics", items: ["Google Analytics 4", "Search Console", "SEMrush / Ahrefs", "Klaviyo / Mailchimp", "HubSpot", "Hotjar"] }
];

const faqs = [
  {
    q: "How much should I budget for digital marketing?",
    a: "It depends on your goals, industry, and growth stage. We recommend allocating 7–15% of revenue for maintenance-level marketing and 15–30% for aggressive growth. We help allocate every dollar to high-impact channels."
  },
  {
    q: "How quickly will I see results?",
    a: "Paid ads generate results in 1–2 weeks. Email marketing shows impact within 30 days. SEO is a longer play—expect meaningful growth in 3–6 months. CRO improvements often show impact within 2–4 weeks of testing."
  },
  {
    q: "Do you guarantee specific results or rankings?",
    a: "We don't make guarantees—and you should be cautious of anyone who does. We guarantee a disciplined, data-driven process, transparent reporting, and relentless optimization accountable to shared KPIs."
  },
  {
    q: "Can you work with my existing marketing team or tools?",
    a: "Absolutely. We integrate with in-house teams and tech stacks seamlessly. We can serve as your full marketing department, an extension of your team, or a strategic overlay."
  },
  {
    q: "Which channels do you recommend for my business?",
    a: "There's no one-size-fits-all answer. Our audit determines the optimal channel mix for your specific audience and goals, prioritizing channels based on potential ROI."
  },
  {
    q: "What reporting will I receive?",
    a: "You'll receive a custom real-time dashboard, weekly performance snapshots, and a monthly report with deep analysis. Every metric is tied to revenue impact—not vanity numbers."
  },
  {
    q: "What does pricing look like?",
    a: "We offer flexible models: monthly retainers (most common), project-based setups, and performance structures for qualified partners. Every engagement starts with an audit and transparent proposal."
  }
];

// --- SUBCOMPONENTS ---

const SectionHeading = ({ subtitle, title, dark = false }) => (
  <div className="mb-16 md:mb-24">
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className={`text-sm font-body uppercase tracking-widest mb-6 ${dark ? 'text-white/60' : 'text-black/60'}`}
    >
      // {subtitle}
    </motion.div>
    <BlurText 
      text={title} 
      className={`font-heading italic text-5xl md:text-7xl leading-[0.9] tracking-[-2px] ${dark ? 'text-white' : 'text-black'}`}
      justify="flex-start"
    />
  </div>
);

const EcosystemDiagram = () => (
  <div className="relative w-full max-w-5xl mx-auto py-12 md:py-24">
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center relative z-10">
      
      {/* Left Column - Acquisition */}
      <div className="flex flex-col gap-8">
        <motion.div 
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="liquid-glass p-6 rounded-2xl relative group"
        >
          <div className="absolute top-1/2 -right-8 w-8 border-t border-dashed border-black/20 hidden md:block group-hover:border-black/50 transition-colors"></div>
          <h4 className="font-heading italic text-2xl mb-2">Paid Ads</h4>
          <p className="text-xs text-black/60 uppercase tracking-wider">Targeted Traffic</p>
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="liquid-glass p-6 rounded-2xl relative group"
        >
          <div className="absolute top-1/2 -right-8 w-8 border-t border-dashed border-black/20 hidden md:block group-hover:border-black/50 transition-colors"></div>
          <h4 className="font-heading italic text-2xl mb-2">SEO</h4>
          <p className="text-xs text-black/60 uppercase tracking-wider">Organic Traffic</p>
        </motion.div>
      </div>

      {/* Center - Revenue Engine */}
      <motion.div 
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        className="relative aspect-square flex items-center justify-center"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-black/5 to-transparent rounded-full animate-[spin_10s_linear_infinite]"></div>
        <div className="absolute inset-4 border border-black/10 rounded-full border-dashed animate-[spin_15s_linear_infinite_reverse]"></div>
        <div className="liquid-glass-strong w-48 h-48 rounded-full flex flex-col items-center justify-center text-center p-6 shadow-2xl relative z-10">
          <SafeIcon icon={FiBarChart2} className="w-8 h-8 mb-3 text-black" />
          <h3 className="font-heading italic text-3xl leading-none">Revenue<br/>Engine</h3>
        </div>
      </motion.div>

      {/* Right Column - Conversion & Retention */}
      <div className="flex flex-col gap-8">
        <motion.div 
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="liquid-glass p-6 rounded-2xl relative group"
        >
          <div className="absolute top-1/2 -left-8 w-8 border-t border-dashed border-black/20 hidden md:block group-hover:border-black/50 transition-colors"></div>
          <h4 className="font-heading italic text-2xl mb-2">CRO</h4>
          <p className="text-xs text-black/60 uppercase tracking-wider">Conversion</p>
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="liquid-glass p-6 rounded-2xl relative group"
        >
          <div className="absolute top-1/2 -left-8 w-8 border-t border-dashed border-black/20 hidden md:block group-hover:border-black/50 transition-colors"></div>
          <h4 className="font-heading italic text-2xl mb-2">Email</h4>
          <p className="text-xs text-black/60 uppercase tracking-wider">Retention & LTV</p>
        </motion.div>
      </div>

    </div>

    {/* Bottom - Strategy Base */}
    <motion.div 
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="mt-8 md:mt-12 liquid-glass p-6 rounded-2xl text-center max-w-md mx-auto relative"
    >
      <div className="absolute -top-8 left-1/2 w-px h-8 border-l border-dashed border-black/20 hidden md:block"></div>
      <h4 className="font-heading italic text-2xl mb-2">Marketing Strategy</h4>
      <p className="text-xs text-black/60 uppercase tracking-wider">The Foundation</p>
    </motion.div>
  </div>
);

const FAQItem = ({ faq, index }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-black/10 last:border-0">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-8 flex items-center justify-between text-left group"
      >
        <span className="font-heading italic text-3xl md:text-4xl text-black group-hover:text-black/70 transition-colors pr-8">
          {faq.q}
        </span>
        <div className="liquid-glass w-12 h-12 rounded-full flex items-center justify-center shrink-0 transition-transform duration-300" style={{ transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)' }}>
          <SafeIcon icon={FiPlus} className="w-5 h-5" />
        </div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <p className="pb-8 text-black/70 font-body leading-relaxed max-w-3xl">
              {faq.a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// --- MAIN COMPONENT ---

export default function DigitalMarketing() {
  
  useEffect(() => {
    document.title = "Digital Marketing Services | Sans Titre";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.content = "Data-driven digital marketing — paid advertising, SEO, email marketing, CRO, and marketing strategy engineered for measurable, scalable revenue.";
    }
  }, []);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a
      }
    }))
  };

  return (
    <div className="outpost-page min-h-screen text-black font-body selection:bg-black/10">
      <style>{outpostStyles}</style>
      <OutpostNavbar />

      {/* Inject FAQ Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      {/* SECTION 1 - HERO */}
      <section className="relative min-h-screen w-full flex flex-col justify-center overflow-hidden pt-20 px-8 md:px-16 lg:px-20">
        {/* Background Image with motion */}
        <motion.div 
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 20, ease: "linear", repeat: Infinity, repeatType: "reverse" }}
          className="absolute inset-0 z-0"
        >
          <img 
            src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=2940&auto=format&fit=crop" 
            alt="Digital Marketing Dashboard" 
            className="w-full h-full object-cover opacity-20 filter grayscale"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/50 via-white/80 to-white"></div>
        </motion.div>

        <div className="relative z-10 max-w-5xl mt-20">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full liquid-glass text-xs font-semibold uppercase tracking-widest mb-8"
          >
            <span className="w-2 h-2 rounded-full bg-black animate-pulse"></span>
            Performance Marketing
          </motion.div>
          
          <h1 className="font-heading italic text-7xl md:text-8xl lg:text-[8rem] leading-[0.85] tracking-[-4px] mb-8">
            Digital <br/> Marketing.
          </h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="text-lg md:text-xl text-black/70 max-w-2xl leading-relaxed mb-10 font-light"
          >
            <strong className="font-medium text-black">Precision-targeted growth.</strong> We engineer data-driven marketing strategies and paid advertising campaigns that transform audience attention into measurable, scalable revenue. Every dollar tracked. Every channel optimized. Every decision backed by data.
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
          >
            <Link to="/contact" className="inline-flex items-center gap-3 liquid-glass-strong rounded-full px-8 py-4 text-sm font-semibold uppercase tracking-widest hover:bg-black hover:text-white transition-all duration-300 group">
              Get Your Marketing Audit
              <SafeIcon icon={FiArrowRight} className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* SECTION 2 - PROBLEM STATEMENT */}
      <section className="py-24 px-8 md:px-16 lg:px-20 bg-white">
        <div className="max-w-7xl mx-auto">
          <SectionHeading subtitle="The Reality" title="Why Most Marketing Spend Gets Wasted." />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {stats.map((stat, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="liquid-glass p-8 rounded-3xl"
              >
                <div className="font-heading italic text-6xl md:text-7xl mb-4">{stat.value}</div>
                <p className="text-black/70 text-sm leading-relaxed">{stat.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="liquid-glass-strong rounded-3xl p-8 md:p-12 text-center max-w-4xl mx-auto"
          >
            <p className="text-xl md:text-2xl font-heading italic leading-relaxed">
              "Marketing isn't an expense — it's a revenue engine. But only when every channel, every campaign, and every dollar is engineered with precision. <strong className="font-serif">That's what we do.</strong>"
            </p>
          </motion.div>
        </div>
      </section>

      {/* SECTION 3 - OUR APPROACH */}
      <section className="py-24 px-8 md:px-16 lg:px-20 bg-[#f8f8f8]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-16 items-center">
            
            <div className="w-full lg:w-1/2">
              <SectionHeading subtitle="Strategic Differentiator" title="Revenue-Engineered Marketing." />
              <div className="space-y-8 text-black/70 font-light leading-relaxed">
                <p className="text-lg text-black font-medium">
                  We don't run campaigns and hope for the best. We build integrated marketing systems where every channel feeds the next, every touchpoint is tracked, and every optimization compounds over time.
                </p>
                
                <div>
                  <h4 className="font-bold text-black mb-2 uppercase tracking-widest text-xs">1. Full-Funnel Architecture</h4>
                  <p>We map your entire customer journey — from first impression to final purchase and beyond — then engineer marketing touchpoints at every stage. Nothing is left to chance.</p>
                </div>
                
                <div>
                  <h4 className="font-bold text-black mb-2 uppercase tracking-widest text-xs">2. Channel Integration, Not Isolation</h4>
                  <p>Paid advertising, SEO, email marketing, and CRO don't live in silos. We design them as an interconnected revenue system — where paid acquisition drives traffic, SEO reduces CAC, and email nurtures.</p>
                </div>

                <div>
                  <h4 className="font-bold text-black mb-2 uppercase tracking-widest text-xs">3. Relentless Optimization</h4>
                  <p>We test, measure, and refine continuously. A/B tests, attribution modeling, and cohort analysis ensure your marketing gets smarter and more efficient every single week.</p>
                </div>

                <Link to="/contact" className="inline-flex items-center gap-2 text-black font-bold uppercase tracking-widest text-sm hover:opacity-70 transition-opacity mt-4 group">
                  See How We'd Grow Your Revenue 
                  <SafeIcon icon={FiArrowRight} className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>

            <div className="w-full lg:w-1/2">
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="relative rounded-[2rem] overflow-hidden aspect-[4/5] liquid-glass p-2"
              >
                <img 
                  src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=2426&auto=format&fit=crop" 
                  alt="Analytics Strategy" 
                  className="w-full h-full object-cover rounded-[1.5rem] filter grayscale contrast-125"
                />
                <div className="absolute inset-0 bg-black/10 rounded-[1.5rem]"></div>
                
                {/* Floating UI Element */}
                <div className="absolute bottom-8 left-8 right-8 liquid-glass-strong rounded-2xl p-6 backdrop-blur-xl">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs uppercase tracking-widest font-semibold">Revenue Growth</span>
                    <SafeIcon icon={FiTrendingUp} className="w-4 h-4 text-green-600" />
                  </div>
                  <div className="h-16 flex items-end gap-2">
                    {[30, 45, 35, 60, 55, 80, 100].map((h, i) => (
                      <motion.div 
                        key={i}
                        initial={{ height: 0 }}
                        whileInView={{ height: `${h}%` }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.1, duration: 0.5 }}
                        className="flex-1 bg-black rounded-t-sm"
                      ></motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>

          </div>
        </div>
      </section>

      {/* SECTION 4 - CAPABILITIES */}
      <section className="py-24 px-8 md:px-16 lg:px-20 bg-white">
        <div className="max-w-7xl mx-auto">
          <SectionHeading subtitle="Core Capabilities" title="The Growth Arsenal." />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {capabilities.map((cap, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className={`liquid-glass p-8 rounded-3xl flex flex-col ${idx === 0 || idx === 1 ? 'lg:col-span-1' : ''} ${idx === 2 ? 'lg:col-span-1' : ''}`}
              >
                <div className="w-12 h-12 rounded-full bg-black text-white flex items-center justify-center mb-6">
                  <SafeIcon icon={cap.icon} className="w-5 h-5" />
                </div>
                <h3 className="font-heading italic text-3xl mb-4">{cap.title}</h3>
                <p className="text-black/70 text-sm leading-relaxed mt-auto">{cap.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5 & 6 - BENEFITS & ECOSYSTEM */}
      <section className="py-24 px-8 md:px-16 lg:px-20 bg-[#f8f8f8] overflow-hidden">
        <div className="max-w-7xl mx-auto">
          
          <div className="text-center mb-16">
            <SectionHeading subtitle="Channel Ecosystem" title="How It All Works Together." />
          </div>

          <EcosystemDiagram />

          <div className="text-center max-w-3xl mx-auto mt-12 mb-24">
            <p className="text-lg text-black/70 leading-relaxed font-light">
              "Most agencies run channels independently. We engineer them as an integrated system — where every channel amplifies the next and every data point informs the whole. The result: compounding growth, declining costs, and revenue that scales."
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {benefits.map((benefit, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="flex items-start gap-4 liquid-glass p-6 rounded-2xl"
              >
                <div className="mt-1 shrink-0 w-6 h-6 rounded-full bg-black text-white flex items-center justify-center">
                  <SafeIcon icon={FiCheck} className="w-3 h-3" />
                </div>
                <p className="text-sm font-medium leading-relaxed">{benefit}</p>
              </motion.div>
            ))}
          </div>

        </div>
      </section>

      {/* SECTION 7 - PROCESS */}
      <section className="py-24 px-8 md:px-16 lg:px-20 bg-black text-white">
        <div className="max-w-7xl mx-auto">
          <SectionHeading subtitle="Our Process" title="Engineered for Scale." dark={true} />
          
          <div className="relative border-l border-white/20 ml-4 md:ml-8 space-y-16">
            {processSteps.map((step, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="relative pl-12 md:pl-20"
              >
                <div className="absolute -left-5 top-0 w-10 h-10 rounded-full bg-black border border-white/20 flex items-center justify-center font-heading italic text-xl">
                  {step.step}
                </div>
                <h3 className="font-heading italic text-4xl mb-4">{step.title}</h3>
                <p className="text-white/60 text-lg leading-relaxed max-w-2xl font-light">
                  {step.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 8 - METRICS */}
      <section className="py-24 px-8 md:px-16 lg:px-20 bg-white">
        <div className="max-w-7xl mx-auto">
          <SectionHeading subtitle="Data & Proof" title="The Numbers That Matter." />
          
          <div className="flex flex-col gap-4 mb-12">
            {metrics.map((item, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8 liquid-glass p-6 rounded-2xl hover:bg-black/5 transition-colors"
              >
                <div className="md:w-1/3 font-heading italic text-2xl md:text-3xl shrink-0">
                  {item.metric}
                </div>
                <div className="w-full h-px bg-black/10 md:hidden"></div>
                <div className="md:w-2/3 text-black/70 text-sm leading-relaxed">
                  {item.insight}
                </div>
              </motion.div>
            ))}
          </div>

          <div className="liquid-glass-strong rounded-2xl p-6 text-center text-sm font-medium tracking-wide uppercase">
            We don't report vanity metrics. Every dashboard centers on revenue, ROAS, CAC, LTV, and profit.
          </div>
        </div>
      </section>

      {/* SECTION 9 - PLATFORMS */}
      <section className="py-24 px-8 md:px-16 lg:px-20 bg-[#f8f8f8]">
        <div className="max-w-7xl mx-auto text-center">
          <SectionHeading subtitle="Tech Stack" title="Platforms We Master." />
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            {platforms.map((group, idx) => (
              <div key={idx} className="flex flex-col gap-6">
                <h4 className="text-xs font-bold uppercase tracking-widest text-black/50">{group.category}</h4>
                <div className="flex flex-wrap justify-center gap-4">
                  {group.items.map((item, i) => (
                    <div key={i} className="liquid-glass px-6 py-3 rounded-full text-sm font-semibold tracking-wide">
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <p className="mt-16 text-black/60 text-sm font-light max-w-2xl mx-auto">
            "We're platform-agnostic and tool-flexible. We go where your customers are and use whatever technology gives you the sharpest competitive edge."
          </p>
        </div>
      </section>

      {/* SECTION 10 - FAQ */}
      <section className="py-24 px-8 md:px-16 lg:px-20 bg-white">
        <div className="max-w-4xl mx-auto">
          <SectionHeading subtitle="Answers" title="Common Questions." />
          <div className="border-t border-black/10">
            {faqs.map((faq, idx) => (
              <FAQItem key={idx} faq={faq} index={idx} />
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 11 - FINAL CTA */}
      <section className="py-24 px-8 md:px-16 lg:px-20 bg-white">
        <div className="max-w-5xl mx-auto relative">
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="liquid-glass-strong rounded-[3rem] p-12 md:p-20 text-center relative overflow-hidden"
          >
            {/* Abstract Background Element */}
            <div className="absolute top-0 right-0 -mr-20 -mt-20 w-64 h-64 bg-black/5 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 bg-black/5 rounded-full blur-3xl"></div>

            <div className="relative z-10">
              <h2 className="font-heading italic text-5xl md:text-7xl leading-[0.9] mb-8 tracking-[-2px]">
                Ready to Turn Marketing <br className="hidden md:block" /> Into Your Growth Engine?
              </h2>
              <p className="text-black/70 text-lg max-w-2xl mx-auto mb-10 leading-relaxed font-light">
                Every day without a data-driven marketing strategy is a day your competitors are capturing the customers you should be winning. Let's engineer your growth.
              </p>
              <Link 
                to="/contact" 
                className="inline-flex items-center gap-3 bg-black text-white rounded-full px-8 py-4 text-sm font-semibold uppercase tracking-widest hover:bg-black/80 transition-colors group"
              >
                Get My Marketing Audit
                <SafeIcon icon={FiArrowRight} className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

    </div>
  );
}